import React, { useEffect, useMemo, useState } from "react";
import styles from "./languageSelectorModal.module.scss";
import Button from "@Components/Button";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import useLanguagePath from "hooks/useLanguagePath";
import useLanguageSelector from "hooks/useLanguageSelector";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { getPickerLanguages } from "@Utils/languageCore";
import useIsApp from "hooks/useIsApp";
import currencyData, { ALLOWED_CURRENCY_CODES } from "constants/currencyConstants";
import setCookie from "helpers/setCookie";
import getCookie from "helpers/getCookie";
import config from "@Config/index";
import { renderChinaLanguageLabel } from "./chinaLanguageLabels";

interface LanguageSelectorModalProps {
  onClose: () => void;
  isCurrencyFeatureEnabled: boolean;
}

const LanguageSelectorModal: React.FC<LanguageSelectorModalProps> = ({
  onClose,
  isCurrencyFeatureEnabled,
}) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();

  // Never empty: URL prefix, else redux / "zh" on .cn, else "en".
  const { userLanguage: preferredLanguage } = useLanguagePath();
  const isMiniProgram = useIsMiniProgram();
  const isApp = useIsApp();
  const showNewLocales = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_NEW_LOCALES);

  const languages = getPickerLanguages({
    currentLanguage: preferredLanguage,
    showNewLocales,
    isMiniProgram,
    isApp,
  });
  const defaultLanguage = languages.some((lang) => lang.code === preferredLanguage)
    ? preferredLanguage
    : languages[0].code;

  const handleEventEmitter = ({ action, ...rest }: any) => {
    gtagEventEmitter({ action, category: "LANGUAGE_MODAL", ...rest });
  };
  const { handleLanguageChange } = useLanguageSelector({ handleEventEmitter });

  const [activeTab, setActiveTab] = useState<"language" | "currency">("language");
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [selectedCurrency, setSelectedCurrency] = useState("default");
  const [currencies, setCurrencies] = useState<typeof currencyData>([]);

  /** Filter currencies based on feature + allowed list */
  const filteredCurrencies = useMemo(() => {
    if (!isCurrencyFeatureEnabled) return [];
    return currencyData.filter((cur) => ALLOWED_CURRENCY_CODES.includes(cur.standard_name));
  }, [isCurrencyFeatureEnabled]);

  /** Sync language */
  useEffect(() => {
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage]);

  /** Setup currency list + cookie ordering */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled) return;

    const userCurrencyCookie = getCookie("user_currency");

    const orderedCurrencies = [...filteredCurrencies];

    if (userCurrencyCookie) {
      setSelectedCurrency(userCurrencyCookie);

      orderedCurrencies.sort((a, b) => {
        if (a.standard_name === userCurrencyCookie) return -1;
        if (b.standard_name === userCurrencyCookie) return 1;
        return 0;
      });
    }

    setCurrencies(orderedCurrencies);
  }, [filteredCurrencies, isCurrencyFeatureEnabled]);

  /** Safety: reset tab if feature disabled */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled && activeTab === "currency") {
      setActiveTab("language");
    }
  }, [isCurrencyFeatureEnabled, activeTab]);

  const onApply = () => {
    // Close modal first to ensure clean state before navigation
    onClose();

    // Whitelabel applies every preference the user changed, not only the open tab: picking a
    // language and then a currency must apply both (POD3-2239). Other builds keep applying the
    // open tab only. The currency cookie is written first so the language navigation (a full
    // page load) already renders in the new currency.
    const applyAllChanges = config.IS_WHITELABEL_APP;
    const currencyChanged =
      isCurrencyFeatureEnabled && selectedCurrency !== (getCookie("user_currency") || "default");
    const applyLanguage =
      activeTab === "language" || (applyAllChanges && selectedLanguage !== defaultLanguage);
    const applyCurrency = activeTab === "currency" || (applyAllChanges && currencyChanged);

    if (applyCurrency) {
      handleEventEmitter({ action: "APPLY_CURRENCY", currency: selectedCurrency });
      const baseDomain = window.location.hostname.split(".").slice(-2).join(".");
      if (selectedCurrency === "default") {
        // delete cookie if exists
        setCookie({
          cname: "user_currency",
          cvalue: "",
          exdays: -1, // expire immediately
          secure: true,
          domain: `.${baseDomain}`,
        });
      } else {
        // set cookie
        setCookie({
          cname: "user_currency",
          cvalue: selectedCurrency,
          exdays: 365,
          secure: true,
          domain: `.${baseDomain}`,
        });
      }
    }

    if (applyLanguage) {
      handleEventEmitter({ action: "APPLY_LANGUAGE", language: selectedLanguage });

      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        handleLanguageChange(selectedLanguage);
      }, 100);
    } else {
      // Use setTimeout to ensure modal closes before reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  const onReset = () => {
    setSelectedLanguage(defaultLanguage);
    if (isCurrencyFeatureEnabled) setSelectedCurrency("default");
    handleEventEmitter({ action: "reset_preferences" });
  };

  const handleActiveTabChange = (tab: "language" | "currency") => {
    setActiveTab(tab);
    handleEventEmitter({ action: `clicked_${tab}_tab` });
  };

  const isChinaUI = config.IS_CHINA_APP;

  return (
    <div className={formatClassNames(styles.container, isChinaUI && styles.china)}>
      <div className={styles.modal_header}>
        <h4>{t("Select Preferences")}</h4>
      </div>

      <div className={formatClassNames(styles.body, isDesktop && styles.desktop)}>
        <div className={styles.sidebar}>
          <div
            className={formatClassNames(
              styles.sidebarItem,
              activeTab === "language" && styles.active,
            )}
            onClick={() => handleActiveTabChange("language")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleActiveTabChange("language");
            }}
            role="button"
            tabIndex={0}
          >
            {t("Language")}
            <span className={styles.checkIcon}>{activeTab === "language" ? "✓" : ""}</span>
          </div>

          {isCurrencyFeatureEnabled && (
            <div
              className={formatClassNames(
                styles.sidebarItem,
                activeTab === "currency" && styles.active,
              )}
              onClick={() => handleActiveTabChange("currency")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleActiveTabChange("currency");
              }}
              role="button"
              tabIndex={0}
            >
              {t("Currency")}
              <span className={styles.checkIcon}>{activeTab === "currency" ? "✓" : ""}</span>
            </div>
          )}
        </div>

        <div className={styles.content}>
          <ul className={styles.list}>
            {activeTab === "language" &&
              languages.map((lang) => (
                <li key={lang.code}>
                  <button
                    type="button"
                    className={formatClassNames(
                      styles.option,
                      selectedLanguage === lang.code && styles.optionActive,
                    )}
                    onClick={() => setSelectedLanguage(lang.code)}
                  >
                    {(isChinaUI && renderChinaLanguageLabel(lang.code, styles)) || (
                      <span className={styles.left}>
                        {lang.flag &&
                          typeof lang.flag === "function" &&
                          React.createElement(lang.flag)}
                        <span className={styles.optionLabel}>{lang.label}</span>
                      </span>
                    )}
                    <span
                      className={formatClassNames(
                        styles.radio,
                        selectedLanguage === lang.code && styles.checked,
                      )}
                    />
                  </button>
                </li>
              ))}

            {isCurrencyFeatureEnabled &&
              activeTab === "currency" &&
              currencies.map((cur, index) => (
                <React.Fragment key={cur.standard_name}>
                  {index === 0 && (
                    <li>
                      <button
                        type="button"
                        className={formatClassNames(
                          styles.option,
                          selectedCurrency === "default" && styles.optionActive,
                        )}
                        onClick={() => setSelectedCurrency("default")}
                      >
                        <span className={formatClassNames(styles.left, styles.optionLabel)}>
                          Default
                        </span>
                        <span
                          className={formatClassNames(
                            styles.radio,
                            selectedCurrency === "default" && styles.checked,
                          )}
                        />
                      </button>
                    </li>
                  )}

                  <li>
                    <button
                      type="button"
                      className={formatClassNames(
                        styles.option,
                        selectedCurrency === cur.standard_name && styles.optionActive,
                      )}
                      onClick={() => setSelectedCurrency(cur.standard_name)}
                    >
                      <span className={formatClassNames(styles.left, styles.optionLabel)}>
                        {`${cur.standard_name} (${cur.symbol}) · ${cur?.full_name?.split("(")[0]}`}
                      </span>
                      <span
                        className={formatClassNames(
                          styles.radio,
                          selectedCurrency === cur.standard_name && styles.checked,
                        )}
                      />
                    </button>
                  </li>
                </React.Fragment>
              ))}
          </ul>
        </div>
      </div>

      <div
        className={formatClassNames(styles.modal_footer, isDesktop && styles.modal_footer_desktop)}
      >
        <Button
          className={isChinaUI ? styles.chinaReset : styles.btn_width}
          variant={isDesktop ? "subtle" : "outline"}
          onClick={onReset}
          {...(isDesktop ? { size: "lg" } : {})}
          greyDisabled={
            selectedLanguage === defaultLanguage &&
            (!isCurrencyFeatureEnabled || selectedCurrency === "default")
          }
        >
          <span className={isChinaUI ? undefined : styles.resetText}>{t("Reset")}</span>
        </Button>
        <Button
          className={isChinaUI ? styles.chinaApply : styles.btn_apply}
          variant="primary"
          onClick={onApply}
          {...(isDesktop ? { size: "lg" } : {})}
        >
          {t("Apply")}
        </Button>
      </div>
    </div>
  );
};

export default wrapperHOC(LanguageSelectorModal, {
  componentName: "LanguageSelectorModal",
  showForChina: true,
});
